/**
 * Deklaracje ZUS / PIT / PPK (spec 01 §9) — enumy i kontrakty API.
 *
 * Deklaracja nie jest osobnym naliczeniem: to ZESTAWIENIE zamkniętych list płac
 * miesiąca. Dlatego kontrakt nie ma tu żadnej stawki ani reguły — wszystko, co
 * liczy, policzył wcześniej silnik płac (ADR-0010), a tu tylko sumujemy i
 * podajemy w układzie wymaganym przez formularz.
 */

/** Dokumenty, które produkuje moduł. KEDU jest FORMATEM dla dra + rca + rsa, nie dokumentem. */
/**
 * Rodzaje dokumentów miesiąca. `rpa` to imienny raport o przychodach należnych
 * za INNY rok kalendarzowy — przy wypłacie z dołu dotyczy każdego stycznia
 * (art. 41 ust. 3 ustawy o systemie ubezpieczeń społecznych, ADR-0018).
 */
/*
 * Rodzaje dokumentów miesiąca oraz — od Etapu 22 — deklaracja ROCZNA płatnika
 * PIT-4R. Rocznik siedzi w tej samej tabeli z miesiącem ustawionym na styczeń
 * roku podatkowego: to nadal jeden dokument z terminem i statusem (ADR-0023).
 */
export const DECLARATION_KINDS = ['dra', 'rca', 'rsa', 'rpa', 'pit4', 'pit4r', 'pit8ar', 'ppk'] as const
export type DeclarationKind = (typeof DECLARATION_KINDS)[number]

/**
 * Rodzaje wchodzące do kompletu MIESIĄCA. PIT-4R jest roczny, więc na ekranie
 * miesiąca nie ma go czego szukać — stoi w osobnej sekcji rocznej (Etap 22).
 */
export const MONTHLY_DECLARATION_KINDS = ['dra', 'rca', 'rsa', 'rpa', 'pit4', 'ppk'] as const
export type MonthlyDeclarationKind = (typeof MONTHLY_DECLARATION_KINDS)[number]

/**
 * Czy rodzaj należy do kompletu miesiąca. Rocznik PIT-4R i PIT-8AR kotwiczy na
 * 1 stycznia roku podatkowego (ADR-0023), więc każdy odczyt kompletu miesiąca
 * musi go odsiać — inaczej przekazany PIT-4R podbija numer kompletu stycznia
 * (#65).
 */
export function isMonthlyDeclarationKind(kind: DeclarationKind): kind is MonthlyDeclarationKind {
  return (MONTHLY_DECLARATION_KINDS as readonly string[]).includes(kind)
}

/**
 * Kody terminu przesyłania deklaracji ze zgłoszenia płatnika (ZUS ZPA albo
 * ZFA), które mają swój punkt w art. 47 ust. 1 ustawy o systemie ubezpieczeń
 * społecznych w brzmieniu od 1.01.2022. Kod wpisuje dział płac i ten sam trafia
 * do bloku I DRA w pliku KEDU, więc termin składek bierze się z NIEGO — a nie
 * z drugiego pola, które mogłoby mu przeczyć (#10). Mapę potwierdza
 * księgowość (ADR-0010); kodu spoza niej nie zgadujemy.
 */
export const ZUS_TERM_CODES = ['1', '3', '6'] as const
export type ZusTermCode = (typeof ZUS_TERM_CODES)[number]

/** Rodzaj płatnika z kodu, czyli punkt art. 47 ust. 1 u.s.u.s. */
export const ZUS_TERM_CODE_PAYER = {
  /** Pkt 2: jednostka budżetowa i samorządowy zakład budżetowy — 5. dzień. */
  '1': 'jednostka_budzetowa',
  /** Pkt 3: płatnik mający osobowość prawną — 15. dzień. */
  '3': 'osoba_prawna',
  /** Pkt 4: pozostali płatnicy — 20. dzień. */
  '6': 'pozostali',
} as const satisfies Record<ZusTermCode, string>
export type ZusPayerKind = (typeof ZUS_TERM_CODE_PAYER)[ZusTermCode]

/** Dni terminów składek z parametrów roku — po jednym na punkt art. 47 ust. 1. */
export interface ZusDeadlineDays {
  /** Pkt 4 — pozostali płatnicy. */
  readonly zusDeadlineDay: number
  /** Pkt 2 — jednostki budżetowe. */
  readonly zusDeadlineDayBudget: number
  /** Pkt 3 — płatnicy mający osobowość prawną. */
  readonly zusDeadlineDayLegalPerson: number
}

export function isZusTermCode(code: string | null | undefined): code is ZusTermCode {
  return code !== null && code !== undefined && (ZUS_TERM_CODES as readonly string[]).includes(code)
}

/**
 * Dzień terminu składek dla kodu terminu płatnika albo `null`, gdy kodu nie
 * ma albo nie ma go w mapie. `null` znaczy „nie znamy terminu" — deklaracja
 * dostaje zastrzeżenie, zamiast udawać 20. dzień za każdą firmę.
 */
export function zusDeadlineDayFor(code: string | null, days: ZusDeadlineDays): number | null {
  if (!isZusTermCode(code)) return null
  switch (ZUS_TERM_CODE_PAYER[code]) {
    case 'jednostka_budzetowa':
      return days.zusDeadlineDayBudget
    case 'osoba_prawna':
      return days.zusDeadlineDayLegalPerson
    case 'pozostali':
      return days.zusDeadlineDay
  }
}

/**
 * Czy rok podatkowy już się skończył (#70). Deklaracje i informacje roczne
 * składa się PO roku — PIT-4R i PIT-8AR do końca stycznia (art. 38 ust. 1a
 * i art. 42 ust. 1a ustawy o PIT), PIT-11 do końca stycznia albo lutego
 * (art. 39 ust. 1), IFT-1R do końca lutego (art. 42 ust. 2 pkt 2). Wystawienie
 * w trakcie roku przepisy znają (np. PIT-11 na wniosek po ustaniu stosunku
 * pracy), więc to nie blokada, tylko świadome potwierdzenie. `today` to dzień
 * w strefie Polski (`warsawDay`).
 */
export function taxYearClosed(year: number, today: string): boolean {
  return Number(today.slice(0, 4)) > year
}

/**
 * Powody, dla których dokument nie może powstać albo wymaga uwagi. Jak przy
 * płacach: serwer oddaje KLUCZ i parametry, zdanie składa i18n.
 */
export const DECLARATION_ISSUE_KINDS = [
  /** Miesiąc nie ma ani jednej zamkniętej listy płac — nie ma z czego liczyć. */
  'brak_zamknietej_listy',
  /** Pracownik bez numeru PESEL w aktach — raport imienny go nie zniesie. */
  'brak_pesel',
  /** Firma bez NIP albo REGON — płatnika nie da się zidentyfikować w KEDU. */
  'brak_danych_platnika',
  /** Brak kodu terminu przysyłania deklaracji ze zgłoszenia płatnika. */
  'brak_kodu_terminu',
  /**
   * Kod terminu jest wpisany, ale nie ma go w mapie `ZUS_TERM_CODES` — termin
   * składek zostaje nieznany. Osobny rodzaj, bo zdanie „brak kodu" byłoby
   * nieprawdą: kod jest, tylko nie wiemy, który punkt art. 47 ust. 1 wyraża.
   */
  'kod_terminu_nieznany',
  /** Brak wpisanej składki wypadkowej — deklaracja poszłaby bez stopy procentowej. */
  'brak_stawki_wypadkowej',
  /** Uczestnicy PPK bez deklaracji — wpłaty policzono wg stawek domyślnych. */
  'brak_deklaracji_ppk',
  /** Parametry roku bez potwierdzenia księgowości (ADR-0010). */
  'parametry_niepotwierdzone',
  /**
   * Ktoś ma przychód należny za więcej niż trzy inne lata. Formularz ZUS RPA ma
   * trzy pary pól (rok, kwota) w bloku III.B, więc reszty nie zmieścimy —
   * mówimy to wprost, zamiast po cichu obciąć.
   */
  'rpa_za_duzo_lat',
  /**
   * Osoba była w PRZEKAZANYM raporcie imiennym, a wypadła z listy płac. ZUS ma
   * jej składki na koncie i sam ich nie wycofa — potrzebny jest raport
   * korygujący z zerową podstawą albo wycofanie kompletu w Płatniku. Modułu
   * do tego nie mamy, więc mówimy wprost, kogo dotyczy.
   */
  'korekta_osoba_bez_raportu',
  /**
   * W miesiącu pobrano podatek zryczałtowany (art. 30 ust. 1 pkt 5a) albo
   * u źródła od nierezydenta (art. 29 ust. 1 pkt 1). Nie wchodzi do zaliczek
   * PIT-4R, tylko do PIT-8AR, a wpłaca się go osobno do 20. dnia miesiąca po
   * pobraniu (art. 42 ust. 1 ustawy o PIT) — proces miesięczny o nim milczał
   * (#3). Nie blokuje: to przypomnienie o drugiej wpłacie, nie brak danych.
   */
  'podatek_zryczaltowany_osobno',
  /**
   * Deklarację roczną wystawiono ze świadomym potwierdzeniem przed końcem roku
   * podatkowego (#70): miesiące, które jeszcze nie minęły, mają zera. Po końcu
   * roku taki dokument jest nieaktualny i trzeba go wystawić ponownie.
   */
  'rok_niezamkniety',
] as const
export type DeclarationIssueKind = (typeof DECLARATION_ISSUE_KINDS)[number]

export interface DeclarationIssue {
  readonly kind: DeclarationIssueKind
  readonly params: Readonly<Record<string, string | number>>
  /** `true` = dokument nie powstaje; `false` = powstaje, ale z zastrzeżeniem. */
  readonly blocking: boolean
}

/** Kroki procesu miesięcznego ze spec 01 §9 — kolejność jest kolejnością na ekranie. */
export const DECLARATION_STEPS = ['payroll', 'zus', 'send', 'pit', 'ppk', 'archive'] as const
export type DeclarationStep = (typeof DECLARATION_STEPS)[number]

/** Stan kroku liczony z faktów, nigdy z listy w kodzie. */
export const DECLARATION_STEP_STATES = ['done', 'current', 'todo'] as const
export type DeclarationStepState = (typeof DECLARATION_STEP_STATES)[number]

/** Podstawa i kwota jednego ubezpieczenia — wspólny kształt dla DRA i RCA. */
export interface ContributionLine {
  /** Podstawa wymiaru w groszach. */
  readonly baseGr: number
  /** Część finansowana przez ubezpieczonego. */
  readonly employeeGr: number
  /** Część finansowana przez płatnika. */
  readonly employerGr: number
}

/** Zestawienie ZUS DRA — sumy miesiąca po wszystkich zamkniętych listach. */
export interface DraSummary {
  readonly month: string
  /** Liczba ubezpieczonych, czyli osób z realną podstawą składek (bez B2B). */
  readonly insuredCount: number
  /** Stopa wypadkowa płatnika w punktach bazowych; `null` = nie wpisano. */
  readonly accidentBp: number | null
  readonly pension: ContributionLine
  readonly disability: ContributionLine
  /** Chorobowe finansuje wyłącznie ubezpieczony. */
  readonly sickness: ContributionLine
  /** Wypadkowe finansuje wyłącznie płatnik. */
  readonly accident: ContributionLine
  readonly health: ContributionLine
  readonly laborFund: ContributionLine
  readonly guaranteedBenefits: ContributionLine
  /** Suma składek społecznych do zapłaty (obie strony). */
  readonly socialTotalGr: number
  /**
   * Blok V formularza: świadczenia wypłacone przez płatnika, które rozlicza
   * w ciężar składek. Wynagrodzenia chorobowego z art. 92 k.p. tu NIE MA —
   * finansuje je pracodawca z własnych środków i nikt mu go nie zwraca.
   */
  readonly benefitsSicknessGr: number
  readonly benefitsAccidentGr: number
  /** Wynagrodzenie płatnika za wypłatę zasiłków (0,1 %), pole V.02. */
  readonly payerFeeGr: number
  /** Łączna kwota do potrącenia, pole V.05. */
  readonly deductionTotalGr: number
  /** Suma wszystkiego, co idzie przelewem do ZUS (po potrąceniu bloku V). */
  readonly zusTotalGr: number
}

/** Jeden okres świadczenia albo przerwy w raporcie ZUS RSA (blok III.B). */
export interface RsaEntry {
  /** Kod świadczenia/przerwy z oficjalnego wykazu ZUS (331, 313, 314, 151). */
  readonly zusCode: string
  readonly dateFrom: string
  readonly dateTo: string
  /** Pole 05 formularza: liczba dni zasiłkowych albo liczba wypłat. */
  readonly days: number
  /** Pole 06: kwota wypłacona przez płatnika; 0 gdy świadczenie wypłaca ZUS. */
  readonly amountGr: number
}

/** Wiersz raportu ZUS RSA — jedna osoba może mieć kilka okresów. */
export interface RsaRow {
  readonly employeeId: string
  readonly lastName: string
  readonly firstName: string
  readonly pesel: string | null
  readonly insuranceCode: string
  readonly entries: readonly RsaEntry[]
}

/** Zestawienie raportu ZUS RSA za miesiąc. */
export interface RsaSummary {
  readonly month: string
  readonly peopleCount: number
  readonly entryCount: number
  /** Suma kwot wypłaconych przez płatnika (wynagrodzenia chorobowe i zasiłki). */
  readonly paidGr: number
  readonly rows: readonly RsaRow[]
}

/** Wiersz raportu imiennego ZUS RCA — jedna osoba. */
export interface RcaRow {
  readonly employeeId: string
  readonly lastName: string
  readonly firstName: string
  /** PESEL bez maski — potrzebny w raporcie imiennym; `null` = brak w aktach. */
  readonly pesel: string | null
  /** Kod tytułu ubezpieczenia (np. 011000 dla umowy o pracę). */
  readonly insuranceCode: string
  /** Wymiar czasu pracy jako ułamek, np. „1/1". */
  readonly workingTime: string
  readonly pension: ContributionLine
  readonly disability: ContributionLine
  readonly sickness: ContributionLine
  readonly accident: ContributionLine
  readonly health: ContributionLine
  /** Wpłata pracodawcy do PPK — raport imienny ma na nią osobne pole. */
  readonly ppkEmployerGr: number
  /** Czy przekroczono roczną podstawę emerytalno-rentową w tym miesiącu. */
  readonly annualCapReached: boolean
}

/**
 * Wiersz raportu ZUS RPA: przychód wypłacony w tym miesiącu, ale NALEŻNY za inny
 * rok kalendarzowy. Blok III.B dotyczy podstawy emerytalno-rentowej, III.C —
 * podstawy wypadkowej (poradnik ZUS RPA).
 */
export interface RpaRow {
  readonly employeeId: string
  readonly lastName: string
  readonly firstName: string
  readonly pesel: string | null
  readonly insuranceCode: string
  /** Pary (rok, kwota) do bloku III.B — podstawa emerytalna i rentowa. */
  readonly pensionByYear: readonly { readonly year: string; readonly amountGr: number }[]
  /** Pary (rok, kwota) do bloku III.C — podstawa wypadkowa. */
  readonly accidentByYear: readonly { readonly year: string; readonly amountGr: number }[]
}

/** Zestawienie raportu ZUS RPA za miesiąc wypłaty. */
export interface RpaSummary {
  readonly month: string
  readonly peopleCount: number
  /** Suma przychodów należnych za inne lata — kwota do sprawdzenia na ekranie. */
  readonly amountGr: number
  readonly rows: readonly RpaRow[]
}

/** Zaliczki PIT-4 — jedna kwota do urzędu skarbowego za miesiąc. */
export interface Pit4Summary {
  readonly month: string
  readonly taxpayerCount: number
  readonly advanceGr: number
  /**
   * Podatek zryczałtowany i u źródła pobrany w miesiącu — osobna wpłata do
   * PIT-8AR (#3). Opcjonalne, bo zapisane wcześniej zestawienia go nie mają.
   */
  readonly lumpSumTaxGr?: number
}

/** Wpłaty PPK per uczestnik — plik dla instytucji finansowej. */
export interface PpkRow {
  readonly employeeId: string
  readonly lastName: string
  readonly firstName: string
  readonly employeeGr: number
  readonly employerGr: number
}

/**
 * PIT-4R — roczna deklaracja płatnika o zaliczkach (Etap 22, ADR-0023).
 * Układ z sekcji C formularza PIT-4R(13): dwanaście kolumn miesięcznych,
 * wiersz 1 ze stosunku pracy z liczbą podatników, wiersz 3 z art. 41 (zlecenia),
 * wiersz 5 jako suma, wiersze 10 i 12 jako kwoty do przekazania do urzędu.
 */
export interface Pit4rMonth {
  readonly month: string
  readonly number: number
  readonly row1TaxpayerCount: number
  readonly row1Gr: number
  readonly row3Gr: number
  readonly row5Gr: number
  readonly row10Gr: number
  readonly row12Gr: number
}

export interface Pit4rSummary {
  readonly year: number
  /** Zawsze dwanaście miesięcy — miesiąc bez wypłaty ma zera, nie brak wiersza. */
  readonly months: readonly Pit4rMonth[]
  readonly totalGr: number
  /** Największa liczba podatników w miesiącu — szybki sanity check. */
  readonly maxTaxpayers: number
  /** Termin złożenia: koniec stycznia roku następnego (art. 38 ust. 1a). */
  readonly deadline: string
}

/**
 * PIT-8AR — roczna deklaracja o zryczałtowanym podatku dochodowym (Etap 24,
 * ADR-0025; wiersz 1 od Etapu 52, ADR-0053). Układ z sekcji C formularza
 * PIT-8AR(14): wiersz 1 to należności osób fizycznych bez miejsca zamieszkania
 * w Rzeczypospolitej Polskiej wymienione w art. 29 ustawy, wiersz 6 to
 * należności z art. 13 pkt 2 i 5-9 z umów z osobą niebędącą pracownikiem
 * płatnika (art. 30 ust. 1 pkt 5a ustawy), wiersz 36 suma pobranego ryczałtu,
 * wiersz 38 podatek podlegający przekazaniu.
 */
export interface Pit8arMonth {
  readonly month: string
  readonly number: number
  /**
   * Wiersz 1: podatek u źródła od należności nierezydentów (art. 29 ustawy).
   * Ten sam podatek, który za rok wykazuje IFT-1R — deklaracja mówi ile go
   * pobrano, informacja mówi komu (ADR-0049, ADR-0053).
   */
  readonly row1Gr: number
  /** Wiersz 6: ryczałt od umów do progu. */
  readonly row6Gr: number
  /** Wiersz 36: suma pobranego ryczałtu z wierszy 1-35. */
  readonly row36Gr: number
  /** Wiersz 38: podatek podlegający przekazaniu (36 minus wynagrodzenie płatnika). */
  readonly row38Gr: number
}

export interface Pit8arSummary {
  readonly year: number
  readonly months: readonly Pit8arMonth[]
  readonly totalGr: number
  /** Termin złożenia: koniec stycznia roku następnego (art. 42 ust. 1a). */
  readonly deadline: string
}

export interface PpkSummary {
  readonly month: string
  readonly participantCount: number
  readonly employeeGr: number
  readonly employerGr: number
  readonly rows: readonly PpkRow[]
}

/** Pozycja listy „Wygenerowane pliki" (spec 01 §9). */
export interface DeclarationFileView {
  readonly id: string | null
  readonly kind: DeclarationKind
  readonly month: string
  readonly status: string
  /** Liczba osób objętych dokumentem (RCA, PPK); `null` gdy nie dotyczy. */
  readonly peopleCount: number | null
  /** Kwota dokumentu w groszach; `null` gdy dokument nie niesie kwoty. */
  readonly amountGr: number | null
  /** Ustawowy termin przekazania. */
  readonly deadline: string | null
  readonly generatedAt: string | null
  readonly sentAt: string | null
  readonly issues: readonly DeclarationIssue[]
  /**
   * Numer identyfikatora kompletu: 1 = pierwotny, 2 i dalsze = korygujący
   * (poradnik ZUS, rozdział III). Ten numer wchodzi do pliku KEDU.
   */
  readonly setNumber: number
}

export interface DeclarationStepView {
  readonly key: DeclarationStep
  readonly state: DeclarationStepState
}

export interface DeclarationsResponse {
  /** Miesiąc ROZLICZENIOWY, czyli miesiąc wypłaty (ADR-0013). */
  readonly month: string
  /** Miesiące PRZEPRACOWANE, których wypłata trafiła do tego miesiąca. */
  readonly workedMonths: readonly string[]
  /** Zawsze `true` od Etapu 10 — moduł stoi na realnych danych. */
  readonly engineAvailable: boolean
  /** Czy miesiąc ma zamkniętą listę płac; bez niej nie ma czego zestawiać. */
  readonly payrollClosed: boolean
  /** Miesiące z zamkniętą listą płac — do wyboru okresu. */
  readonly availableMonths: readonly string[]
  readonly steps: readonly DeclarationStepView[]
  /** Numer kompletu, który powstanie przy najbliższym generowaniu. */
  readonly nextSetNumber: number
  readonly files: readonly DeclarationFileView[]
  /** Historia wysyłek: dokumenty oznaczone jako przekazane, najnowsze pierwsze. */
  readonly history: readonly DeclarationFileView[]
  readonly issues: readonly DeclarationIssue[]
}
